import { Chip, Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ERole } from '../enums/user/ERole';
import { ETheme } from '../enums/user/ETheme';
import { formatDate } from '../functions/formatDate';
import { formatPrice } from '../functions/formatPrice';
import { getRefundStatusColor } from '../functions/getChipColors';
import { IRefund } from '../interfaces/refund/IRefund';
import { darkTheme, lightTheme } from '../mui/theme';

export const useRefundColumns = (
  setSelectedItem: (refund: IRefund) => void,
  setShowDeleteModal: (show: boolean) => void,
): GridColDef[] => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'title',
      headerName: t('general.title'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => <div>{params?.row?.title ?? '-'}</div>,
    },
    {
      field: 'amount',
      headerName: t('expenses.price'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => <div>{formatPrice(params?.row?.amount ?? '-')}</div>,
    },
    {
      field: 'updatedAt',
      headerName: t('general.updatedAt'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { updatedAt } = params?.row;
        return <div>{formatDate(updatedAt)}</div>;
      },
    },
    {
      field: 'refundStatus',
      headerName: t('general.status'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <Chip
          label={t(`statuses.${params.value}`)} // Translated status
          color={getRefundStatusColor(params.value)}
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 100,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {/* Edit or View Icon */}
            <Tooltip title={t('general.edit')}>
              <span
                className={'d-flex'}
                onClick={() => {
                  navigate(
                    `/${userData?.role === ERole?.USER ? 'user' : 'admin'}/refund/${params?.row?.id}`,
                  );
                }}
              >
                <EditIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </Tooltip>

            {/* Delete Icon */}

            <Tooltip title={t('general.delete')}>
              <span
                className={'ms-3 d-flex'}
                onClick={() => {
                  setSelectedItem(params?.row);
                  setShowDeleteModal(true);
                }}
              >
                <TrashIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];
};
